import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  BookOpen,
  Boxes,
  Pencil,
  Package,
  Plus,
  Search,
  Sparkles,
  Truck,
  Wrench,
} from 'lucide-react';
import { Heading } from '../layout';
import { FormDrawer } from '../forms';
import { useUIColors } from '../../hooks/useTheme';
import purchaseOrdersAPI from '../../services/purchaseOrdersAPI';
import inventoryAPI from '../../services/inventoryAPI';
import { PO_ITEM_TYPE_ORDER } from './purchaseOrderSummary';

const TYPE_ICONS = {
  'Materias primas': Package,
  Insumos: Boxes,
  Upselling: Sparkles,
  Servicios: Wrench,
  Transporte: Truck,
  Otros: BookOpen,
};

const EMPTY_FORM = {
  item_type: 'Materias primas',
  item_code: '',
  description: '',
  unit: 'UN',
  format_code: '',
  unit_price: 0,
};

const formatMoney = (value) =>
  new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('es-CL');
};

const COLUMNS = [
  { key: 'Código', sortKey: 'item_code', align: 'left' },
  { key: 'Item', sortKey: 'description', align: 'left' },
  { key: 'Formato', sortKey: 'unit', align: 'left' },
  { key: 'Usos en OC', sortKey: 'usage_count', align: 'right' },
  { key: 'Último precio', sortKey: 'last_unit_price', align: 'right' },
  { key: 'Última OC', sortKey: 'last_po_code', align: 'left' },
  { key: 'Última fecha', sortKey: 'last_order_date', align: 'left' },
  { key: '', sortKey: null, align: 'right' },
];

const sortValue = (row, key) => {
  switch (key) {
    case 'usage_count':
    case 'last_unit_price':
      return Number(row[key] || 0);
    case 'last_order_date':
      return String(row.last_order_date || '');
    default:
      return String(row[key] || '').toLowerCase();
  }
};

const CatalogTypePanel = ({
  itemType,
  rows,
  loading,
  error,
  isDarkMode = false,
  onCreate,
  onEdit,
}) => {
  const ui = useUIColors();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState('item_code');
  const [sortDir, setSortDir] = useState('asc');

  const filteredRows = useMemo(() => {
    const type = String(itemType || '').trim().toLowerCase();
    const tokens = searchQuery.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return (rows || [])
      .filter((row) => String(row.item_type || '').trim().toLowerCase() === type)
      .filter((row) => {
        if (!tokens.length) return true;
        const hay = [row.item_code, row.description, row.unit, row.format_code, row.last_po_code]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return tokens.every((token) => hay.includes(token));
      });
  }, [rows, itemType, searchQuery]);

  const sortedRows = useMemo(() => {
    const list = [...filteredRows];
    list.sort((a, b) => {
      const av = sortValue(a, sortColumn);
      const bv = sortValue(b, sortColumn);
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [filteredRows, sortColumn, sortDir]);

  const toggleSort = (key) => {
    if (!key) return;
    if (sortColumn === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortColumn(key);
    setSortDir(
      key === 'usage_count' || key === 'last_unit_price' || key === 'last_order_date' ? 'desc' : 'asc'
    );
  };

  const SortIcon = ({ column }) => {
    if (!column) return null;
    if (sortColumn !== column) return <ArrowUpDown size={12} style={{ opacity: 0.45 }} />;
    return sortDir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />;
  };

  const border = ui.border;
  const text = ui.text;
  const muted = ui.muted;
  const headerBg = ui.header;
  const inputBg = ui.inputBg;
  const inputBorder = ui.inputBorder;

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
          padding: '12px 16px',
          borderBottom: `1px solid ${border}`,
          background: ui.page,
        }}
      >
        <div style={{ position: 'relative', flex: '1 1 220px', minWidth: 180, maxWidth: 380 }}>
          <Search
            size={14}
            style={{
              position: 'absolute',
              left: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              color: muted,
              pointerEvents: 'none',
            }}
          />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar código, ítem, OC…"
            style={{
              boxSizing: 'border-box',
              width: '100%',
              height: 36,
              padding: '0 10px 0 30px',
              borderRadius: 8,
              border: `1px solid ${inputBorder}`,
              background: inputBg,
              color: text,
              fontSize: 13,
              outline: 'none',
            }}
          />
        </div>
        <button
          type="button"
          onClick={() => onCreate?.(itemType)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            height: 36,
            padding: '0 12px',
            borderRadius: 8,
            border: 'none',
            background: '#16a34a',
            color: '#fff',
            fontSize: 12,
            fontWeight: 700,
            cursor: 'pointer',
            flexShrink: 0,
            whiteSpace: 'nowrap',
          }}
        >
          <Plus size={14} /> Nuevo ítem
        </button>
        <span style={{ fontSize: 12, color: muted, flexShrink: 0 }}>
          {loading ? 'Cargando…' : error || `${sortedRows.length} ítems`}
        </span>
      </div>

      <div style={{ overflow: 'hidden', background: ui.page }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 920 }}>
            <thead>
              <tr style={{ background: headerBg, borderBottom: `1px solid ${border}` }}>
                {COLUMNS.map((col) => (
                  <th
                    key={col.key || 'actions'}
                    onClick={() => toggleSort(col.sortKey)}
                    style={{
                      textAlign: col.align,
                      padding: '10px 12px',
                      fontSize: 11,
                      fontWeight: 700,
                      color: muted,
                      cursor: col.sortKey ? 'pointer' : 'default',
                      whiteSpace: 'nowrap',
                      userSelect: 'none',
                    }}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      {col.key}
                      <SortIcon column={col.sortKey} />
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={COLUMNS.length} style={{ padding: 24, color: muted, textAlign: 'center' }}>
                    Cargando catálogo…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td
                    colSpan={COLUMNS.length}
                    style={{ padding: 24, color: '#dc2626', textAlign: 'center' }}
                  >
                    {error}
                  </td>
                </tr>
              ) : sortedRows.length === 0 ? (
                <tr>
                  <td colSpan={COLUMNS.length} style={{ padding: 24, color: muted, textAlign: 'center' }}>
                    No hay ítems codificados para este tipo.
                  </td>
                </tr>
              ) : (
                sortedRows.map((row, index) => (
                  <tr
                    key={row.id || `${row.item_type}-${row.item_code}-${index}`}
                    style={{ borderBottom: `1px solid ${border}` }}
                  >
                    <td style={{ padding: '10px 12px', fontSize: 12, fontWeight: 700, color: text }}>
                      {row.item_code || '—'}
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 12, color: text }}>
                      {row.description || '—'}
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 12, color: muted }}>
                      {row.unit || row.format_code || '—'}
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 12, color: text, textAlign: 'right' }}>
                      {row.usage_count ?? 0}
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 12, color: text, textAlign: 'right' }}>
                      {formatMoney(row.last_unit_price ?? row.unit_price)}
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 12, color: text }}>
                      {row.last_po_code || '—'}
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 12, color: muted }}>
                      {formatDate(row.last_order_date)}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                      <button
                        type="button"
                        onClick={() => onEdit?.(row)}
                        title="Editar ítem"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          height: 30,
                          padding: '0 10px',
                          borderRadius: 6,
                          border: `1px solid ${inputBorder}`,
                          background: inputBg,
                          color: text,
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        <Pencil size={13} /> Editar
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const PaintProcurementCatalogWorkspace = ({ isDarkMode = false }) => {
  const ui = useUIColors();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formatOptions, setFormatOptions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [formError, setFormError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [activeType, setActiveType] = useState(PO_ITEM_TYPE_ORDER[0]);

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await purchaseOrdersAPI.getCatalog();
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setError(err?.message || 'No se pudo cargar el catálogo de ítems.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  useEffect(() => {
    inventoryAPI
      .getFormats()
      .then((data) => setFormatOptions(Array.isArray(data) ? data : []))
      .catch(() => setFormatOptions([]));
  }, []);

  const openCreate = (itemType) => {
    setIsEditing(false);
    setEditingId(null);
    setFormError('');
    setFormData({
      ...EMPTY_FORM,
      item_type: itemType || PO_ITEM_TYPE_ORDER[0],
    });
    setShowForm(true);
  };

  const openEdit = (row) => {
    setIsEditing(true);
    setEditingId(row.id || null);
    setFormError('');
    setFormData({
      item_type: row.item_type || '',
      item_code: row.item_code || '',
      description: row.description || '',
      unit: row.unit || 'UN',
      format_code: row.format_code || '',
      unit_price: row.unit_price ?? row.last_unit_price ?? 0,
    });
    setShowForm(true);
  };

  const handleChange = (key, value) => {
    setFormData((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'unit') {
        const fmt = formatOptions.find((opt) => opt.value === value || opt.label === value);
        if (fmt?.format_code) next.format_code = fmt.format_code;
      }
      return next;
    });
  };

  const handleSave = async () => {
    const description = String(formData.description || '').trim();
    const itemType = String(formData.item_type || '').trim();
    if (!itemType) {
      setFormError('Selecciona un tipo.');
      return;
    }
    if (!description) {
      setFormError('El nombre del ítem es obligatorio.');
      return;
    }
    if (isEditing && !editingId) {
      setFormError('No se puede editar este ítem (falta identificador). Recarga el catálogo.');
      return;
    }

    const payload = {
      item_type: itemType,
      description,
      item_code: String(formData.item_code || '').trim(),
      unit: String(formData.unit || 'UN').trim() || 'UN',
      format_code: String(formData.format_code || '').trim(),
      unit_price: Number(formData.unit_price || 0),
    };

    setIsSaving(true);
    setFormError('');
    try {
      if (isEditing) {
        await purchaseOrdersAPI.updateCatalogItem(editingId, payload);
      } else {
        await purchaseOrdersAPI.createCatalogItem(payload);
      }
      setShowForm(false);
      await loadCatalog();
    } catch (err) {
      setFormError(err?.message || 'No se pudo guardar el ítem.');
    } finally {
      setIsSaving(false);
    }
  };

  const formSections = [
    {
      key: 'main',
      title: 'Datos del ítem',
      flat: true,
      columns: 2,
      fields: [
        {
          key: 'item_type',
          label: 'Tipo',
          type: 'select',
          options: [
            { value: '', label: 'Seleccionar tipo' },
            ...PO_ITEM_TYPE_ORDER.map((label) => ({ value: label, label })),
          ],
        },
        {
          key: 'item_code',
          label: 'Código',
          placeholder: 'Vacío = se asigna automáticamente',
        },
        {
          key: 'description',
          label: 'Item',
          placeholder: 'Nombre del ítem',
        },
        {
          key: 'unit',
          label: 'Formato',
          type: 'select',
          options: [
            { value: '', label: 'Seleccionar formato' },
            ...formatOptions.map((opt) => ({
              value: opt.value || opt.label,
              label: opt.label || opt.value,
            })),
            ...(formData.unit &&
            !formatOptions.some((opt) => opt.value === formData.unit || opt.label === formData.unit)
              ? [{ value: formData.unit, label: formData.unit }]
              : []),
          ],
        },
        {
          key: 'unit_price',
          label: 'Precio referencial',
          type: 'number',
        },
      ],
    },
  ];

  const typeTabs = useMemo(() => {
    const counts = {};
    rows.forEach((row) => {
      const type = String(row.item_type || '').trim() || 'Otros';
      counts[type] = (counts[type] || 0) + 1;
    });
    const known = PO_ITEM_TYPE_ORDER.map((label) => ({
      key: label,
      label,
      count: counts[label] || 0,
      Icon: TYPE_ICONS[label] || BookOpen,
    }));
    const extras = Object.keys(counts)
      .filter((type) => !PO_ITEM_TYPE_ORDER.includes(type))
      .sort()
      .map((label) => ({
        key: label,
        label,
        count: counts[label] || 0,
        Icon: BookOpen,
      }));
    return [...known, ...extras];
  }, [rows]);

  useEffect(() => {
    if (!typeTabs.length) return;
    if (typeTabs.some((tab) => tab.key === activeType)) return;
    setActiveType(typeTabs[0].key);
  }, [typeTabs, activeType]);

  const listSubtitle = loading
    ? 'Cargando…'
    : error || `${rows.length} ítems en catálogo`;

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Heading level={2} isDarkMode={isDarkMode} style={{ marginBottom: 6 }}>
          Catálogo
        </Heading>
        <p style={{ margin: 0, maxWidth: 720, fontSize: 13, color: ui.muted, lineHeight: 1.45 }}>
          Codifica y mantén los ítems de adquisiciones por tipo. Los nuevos códigos quedan disponibles
          al crear órdenes de compra.
        </p>
      </div>

      <div
        style={{
          border: `1px solid ${ui.border}`,
          borderRadius: 12,
          overflow: 'hidden',
          background: ui.page,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: `1px solid ${ui.border}`,
            background: ui.header,
            flexWrap: 'wrap',
            gap: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', minWidth: 0 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: ui.text }}>
              Catálogo de adquisiciones
            </h3>
            <p style={{ margin: 0, fontSize: 12, color: ui.muted }}>{listSubtitle}</p>
          </div>
        </div>

        <div
          role="tablist"
          aria-label="Tipos de catálogo"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
            padding: '0 12px',
            borderBottom: `1px solid ${ui.border}`,
            background: ui.header,
            overflowX: 'auto',
          }}
        >
          {typeTabs.map((item) => {
            const active = activeType === item.key;
            const Icon = item.Icon;
            return (
              <button
                key={item.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setActiveType(item.key)}
                style={{
                  appearance: 'none',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  padding: '10px 12px',
                  marginBottom: -1,
                  fontSize: 13,
                  fontWeight: active ? 700 : 500,
                  color: active ? ui.text : ui.muted,
                  borderBottom: active ? '2px solid #16a34a' : '2px solid transparent',
                  whiteSpace: 'nowrap',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <Icon size={14} />
                <span>{item.label}</span>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: active ? (isDarkMode ? '#86efac' : '#15803d') : ui.muted,
                    background: active
                      ? isDarkMode
                        ? 'rgba(22, 163, 74, 0.2)'
                        : 'rgba(22, 163, 74, 0.12)'
                      : isDarkMode
                        ? 'rgba(255,255,255,0.06)'
                        : 'rgba(0,0,0,0.05)',
                    borderRadius: 999,
                    padding: '1px 7px',
                    minWidth: 18,
                    textAlign: 'center',
                  }}
                >
                  {item.count}
                </span>
              </button>
            );
          })}
        </div>

        <CatalogTypePanel
          itemType={activeType}
          rows={rows}
          loading={loading}
          error={error}
          isDarkMode={isDarkMode}
          onCreate={openCreate}
          onEdit={openEdit}
        />
      </div>

      <FormDrawer
        isOpen={showForm}
        onClose={() => !isSaving && setShowForm(false)}
        onSave={handleSave}
        isSaving={isSaving}
        isDarkMode={isDarkMode}
        title={isEditing ? 'Editar ítem' : 'Nuevo ítem'}
        subtitle={formData.item_type || 'Catálogo de adquisiciones'}
        sections={formSections}
        formData={formData}
        onChange={handleChange}
        formError={formError}
        saveLabel={isEditing ? 'Guardar cambios' : 'Crear ítem'}
        width={520}
      />
    </div>
  );
};

export default PaintProcurementCatalogWorkspace;
